import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { PublicStream } from '@zip2live/shared';
import { Avatar, Header, Link, LiveBadge } from '../components/Brand.tsx';
import { frameOnScreenAt, Player } from '../components/Player.tsx';
import { ShareBar } from '../components/ShareBar.tsx';
import { ChatOverlay, ChatPanel, ChatSheet, chatClosed } from '../components/Chat.tsx';
import { ShareIcon } from '../components/Icons.tsx';
import { StreamStats, WatchingPill } from '../components/StreamStats.tsx';
import { FloatingReactions, ReactionButtons, VoteButtons } from '../components/Reactions.tsx';
import { BlockCreatorLink, BlockedCreator } from '../components/Safety.tsx';
import { removalLabel } from '@zip2live/shared/safety';
import { FollowButton } from '../components/FollowButton.tsx';
import { useStreamStatus } from '../lib/useStreamStatus.ts';
import { useChatIdentity } from '../lib/chat.ts';
import { useBlocks } from '../lib/safety.ts';
import { navigate } from '../lib/router.ts';
import { shareLink } from '../lib/share.ts';
import { api } from '../lib/api.ts';

const useMedia = (query: string) => {
  const [match, setMatch] = useState(() => matchMedia(query).matches);
  useEffect(() => {
    const m = matchMedia(query);
    const on = () => setMatch(m.matches);
    m.addEventListener('change', on);
    return () => m.removeEventListener('change', on);
  }, [query]);
  return match;
};

export function Watch({ id }: { id: string }) {
  const { stream, error } = useStreamStatus(id);
  const [aspect, setAspect] = useState<number | null>(null);
  const [chatSheet, setChatSheet] = useState(false);
  const phone = useMedia('(max-width: 639px)');
  // Chat sits beside the video from here up, under it below.
  const wide = useMedia('(min-width: 1024px)');
  const handle = stream?.channel.handle ? `@${stream.channel.handle}` : null;
  const name = stream?.channel.displayName ?? handle ?? 'The creator';
  const s = stream?.status;
  const playing = (s === 'LIVE' || s === 'RECONNECTING' || s === 'ENDING') && stream?.playbackUrl;
  const replay = s === 'ENDED' && stream?.playbackUrl;
  const waiting = s === 'CREATED' || s === 'PREPARING' || s === 'READY' || s === 'CONNECTING';
  const deleted = Boolean(stream?.deleted);
  // Someone else's private replay: no title, no video. The creator gets both.
  const hiddenReplay = Boolean(stream?.private) && !replay && !stream?.removed && !deleted;
  const title = deleted ? 'Deleted stream' : (stream?.title ?? `${name}’s stream`);
  // You blocked this creator: a notice instead of the video, unless you choose to watch anyway.
  const { creatorBlocked } = useBlocks(id);
  const [watchAnyway, setWatchAnyway] = useState(false);
  const own = Boolean(useChatIdentity(id)?.host);
  const blockedView = Boolean(stream && creatorBlocked && !watchAnyway && !deleted);
  const blockName = handle ?? stream?.channel.displayName ?? 'this creator';
  // A phone held upright watching a vertical stream: the video takes the whole screen.
  // The Player keeps the same place in the tree either way, so switching layouts never reloads it.
  const showPlayer = Boolean(!blockedView && (playing || replay));
  const immersive = showPlayer && phone && aspect !== null && aspect < 1;
  // The upright-phone layout's bottom bar covers the picture's bottom: the player's controls sit above it.
  const bottomBar = useRef<HTMLDivElement>(null);
  const [bottomInset, setBottomInset] = useState(0);
  useEffect(() => {
    const el = bottomBar.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setBottomInset(el.offsetHeight));
    ro.observe(el);
    return () => ro.disconnect();
  }, [immersive]);

  if (error && !stream) {
    return (
      <Dark>
        <div className="mx-auto mt-24 max-w-md px-6 text-center">
          <p className="speed-text text-4xl">Stream not found</p>
          <p className="mt-3 text-muted-dark">Check the link, or start your own.</p>
          <button type="button" onClick={() => navigate('/go-live')} className="btn-race mt-10 h-15 px-9 text-xl">
            Go live yourself
          </button>
        </div>
      </Dark>
    );
  }

  // No badges over the notice shown instead of a blocked creator's video.
  const badge = blockedView ? null : s === 'LIVE' ? <LiveBadge /> : s === 'RECONNECTING' ? <Chip>Reconnecting</Chip> : replay ? <Chip>REPLAY</Chip> : null;
  const watching = (s === 'LIVE' || s === 'RECONNECTING') && stream && !blockedView ? <WatchingPill viewers={stream.viewers} /> : null;
  const showStats = Boolean(stream && !deleted && !hiddenReplay);
  // Likes for any stream people can watch; floating reactions while it's live.
  const canVote = showStats && !stream?.removed && !blockedView;
  const canReact = canVote && (s === 'LIVE' || s === 'RECONNECTING');
  const player = showPlayer && (
    <Player
      key={`${stream!.playbackUrl}-${replay ? 'replay' : 'live'}`}
      src={stream!.playbackUrl!}
      live={!replay}
      streamId={stream!.id}
      poster={replay ? stream!.thumbnailUrl : null}
      immersive={immersive}
      bottomInset={bottomInset}
      onAspect={setAspect}
      title={title}
      // Inside the player so they stay on screen in full screen. The upright phone layout has its own top bar.
      badges={!immersive && badge ? <>{badge}{watching}</> : null}
      overlay={canReact ? <FloatingReactions streamId={stream!.id} /> : null}
    />
  );
  // Chat goes with the video: none for removed, deleted or private streams.
  const showChat = Boolean(stream && !stream.removed && !deleted && !stream.private && !stream.replayExpired && !blockedView);
  const chatOpen = Boolean(stream && !chatClosed(stream));

  return (
    <main className={immersive ? 'fixed inset-0 bg-black text-white' : 'min-h-full bg-navy-900 text-white'}>
      {!immersive && <DarkHeader />}
      <div className={immersive ? 'h-full' : 'mx-auto w-full max-w-6xl sm:px-8 lg:grid lg:grid-cols-[minmax(0,1fr)_340px] lg:items-start lg:gap-6'}>
        <div className={immersive ? 'h-full' : 'min-w-0'}>
          <div className={immersive ? 'relative h-full' : 'relative'}>
            {player || (blockedView ? <BlockedCreator streamId={id} name={blockName} onWatch={() => setWatchAnyway(true)} /> : (
              <div className="flex aspect-video w-full flex-col items-center justify-center bg-navy px-6 text-center sm:rounded-2xl">
                {!stream && <p className="font-semibold text-white/70">Loading…</p>}
                {waiting && (
                  <>
                    <span className="relative flex h-4 w-4" aria-hidden>
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-race opacity-75" />
                      <span className="relative inline-flex h-4 w-4 rounded-full bg-race" />
                    </span>
                    <p className="speed-text mt-5 text-2xl sm:text-4xl">Waiting for {name} to go live</p>
                    <p className="mt-2 text-sm text-muted-dark sm:text-base">Stay on this page. The video starts automatically.</p>
                  </>
                )}
                {deleted && (
                  <>
                    <p className="speed-text text-2xl sm:text-4xl">This stream was deleted</p>
                    <p className="mt-2 text-sm text-muted-dark sm:text-base">Its creator removed it.</p>
                  </>
                )}
                {hiddenReplay && (
                  <>
                    <p className="speed-text text-2xl sm:text-4xl">This replay is private</p>
                    <p className="mt-2 text-sm text-muted-dark sm:text-base">Only {stream?.channel.displayName ?? handle ?? 'its creator'} can watch it.</p>
                  </>
                )}
                {stream?.removed && !deleted && (
                  <>
                    <p className="speed-text text-2xl sm:text-4xl">This broadcast was removed</p>
                    <p className="mt-2 text-sm text-muted-dark sm:text-base">
                      {stream.removalReason === 'copyright' ? 'After a copyright notice from the rights holder.' : `It broke our rules: ${removalLabel(stream.removalReason).toLowerCase()}.`}
                    </p>
                  </>
                )}
                {s === 'ENDED' && stream?.replayExpired && !stream.removed && !hiddenReplay && !deleted && (
                  <>
                    <p className="speed-text text-2xl sm:text-4xl">This replay has expired</p>
                    <p className="mt-2 text-sm text-muted-dark sm:text-base">Replays are kept for 24 hours, or 30 days when the creator is signed in.</p>
                  </>
                )}
                {(s === 'ENDED' || s === 'EXPIRED') && !replay && !stream?.removed && !hiddenReplay && !deleted && !stream?.replayExpired && (
                  <>
                    <p className="speed-text text-2xl sm:text-4xl">This broadcast has ended</p>
                    <p className="mt-2 text-sm text-muted-dark sm:text-base">Thanks for watching.</p>
                  </>
                )}
                {s === 'FAILED' && <p className="speed-text text-2xl sm:text-4xl">This broadcast could not start</p>}
              </div>
            ))}
            {immersive ? (
              <div className="pointer-events-none absolute inset-x-0 top-0 flex items-center justify-between gap-3 bg-black/35 px-3.5 pt-[max(0.75rem,env(safe-area-inset-top))] pb-3">
                <div className="flex min-w-0 items-center gap-2.5">
                  {badge}
                  {watching}
                  {stream?.channel.handle ? (
                    <Link to={`/${stream.channel.handle}/about`} className="pointer-events-auto flex min-w-0 items-center gap-2">
                      <Avatar name={name} photoUrl={stream.channel.photoUrl} className="h-7 w-7 text-xs" />
                      <span className="truncate text-[15px] font-extrabold">{handle}</span>
                    </Link>
                  ) : (
                    <span className="truncate text-[15px] font-extrabold">{handle ?? name}</span>
                  )}
                  {stream && !deleted && !creatorBlocked && <FollowButton target={{ streamId: stream.id }} name={name} size="sm" initialFollowers={stream.channel.followers} className="pointer-events-auto" />}
                </div>
                <Link to="/" className="speed-text pointer-events-auto text-[15px]" aria-label="ZIP2LIVE home">
                  ZIP<span className="text-race-300">2</span>LIVE
                </Link>
              </div>
            ) : (
              badge && !player && (
                <div className="pointer-events-none absolute top-3 left-3 flex items-center gap-2 sm:top-4 sm:left-4">
                  {badge}
                  {watching}
                </div>
              )
            )}
          </div>

          {immersive ? (
            <div ref={bottomBar} className="absolute inset-x-0 bottom-0">
              {canVote && (
                <div className="absolute right-3 bottom-full mb-3 flex flex-col items-center gap-2">
                  <VoteButtons stream={stream!} compact />
                  {canReact && <ReactionButtons streamId={stream!.id} vertical />}
                </div>
              )}
              {showChat && (chatOpen || replay) && <ChatOverlay stream={stream!} onOpen={() => setChatSheet(true)} className="max-h-[32dvh] px-3.5 pb-2.5" />}
              <div className="bg-black/45 px-3.5 pt-3.5 pb-[max(1.75rem,env(safe-area-inset-bottom))]">
                <div className="flex items-center justify-between gap-3">
                  <h1 className="truncate text-[17px] font-extrabold">{title}</h1>
                  {stream && (
                    <span className="flex shrink-0 gap-3">
                      <ReportLink stream={stream} className="text-xs text-white/70" />
                      {!own && !creatorBlocked && <BlockCreatorLink stream={stream} name={blockName} className="text-xs text-white/70" />}
                    </span>
                  )}
                </div>
                {showStats && <StreamStats stream={stream!} className="mt-1.5 !text-white/80" />}
                <div className="mt-3 flex gap-2">
                  {showChat && (
                    <button type="button" onClick={() => setChatSheet(true)} className="h-12 min-w-0 flex-1 truncate rounded-xl bg-white/15 px-4 text-left text-[15px] font-semibold text-white/85">
                      {chatOpen ? 'Say something…' : 'Chat replay'}
                    </button>
                  )}
                  <button type="button" aria-label="Share" onClick={() => void shareLink(stream!.watchUrl)} className={`inline-flex h-12 items-center justify-center rounded-xl bg-white font-extrabold text-navy ${showChat ? 'w-12' : 'flex-1'}`}>
                    {showChat ? <ShareIcon /> : 'Share'}
                  </button>
                  <button type="button" onClick={() => navigate('/go-live')} className={`speed-text h-12 rounded-xl bg-race text-[17px] ${showChat ? 'px-4' : 'flex-1'}`}>
                    {showChat ? 'Go live' : 'Go live too'}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="px-4 pt-5 pb-12 sm:px-0">
              <div className="flex flex-wrap items-start justify-between gap-6">
                <div className="min-w-0 flex-[999_1_380px]">
                  <h1 className="text-xl font-extrabold sm:text-[28px]">{title}</h1>
                  {stream && !deleted && <CreatorStrip stream={stream} replay={Boolean(replay)} canFollow={!creatorBlocked} />}
                  {showStats && <StreamStats stream={stream!} className="mt-3" />}
                  {canVote && (
                    <div className="mt-4 flex flex-wrap items-center gap-2">
                      <VoteButtons stream={stream!} />
                      {canReact && <span className="mx-1 hidden h-6 w-px bg-white/15 sm:block" aria-hidden />}
                      {canReact && <ReactionButtons streamId={stream!.id} />}
                    </div>
                  )}
                </div>
                {stream && !deleted && !hiddenReplay && (wide || !showChat) && (
                  <div className="min-w-0 flex-[1_1_340px]">
                    <ShareBar url={stream.watchUrl} dark compact label="Stream link" />
                  </div>
                )}
              </div>
              {stream && showChat && !wide && (
                <>
                  <ChatPanel stream={stream} className="mt-5 h-[min(64dvh,560px)] min-h-[360px] rounded-2xl" />
                  <div className="mt-6">
                    <ShareBar url={stream.watchUrl} dark compact label="Stream link" />
                  </div>
                </>
              )}

              <div className="mt-10 flex flex-wrap items-center justify-between gap-5 rounded-3xl bg-navy p-6 sm:p-7">
                <div>
                  <p className="speed-text text-2xl sm:text-[28px]">Your turn.</p>
                  <p className="mt-1 text-muted-dark">Go live from this {phone ? 'phone' : 'browser'} and get your own link in seconds. No signup.</p>
                </div>
                <button type="button" onClick={() => navigate('/go-live')} className="btn-race shadow-race-deep h-14 w-full px-8 text-xl sm:w-auto">
                  GO LIVE
                </button>
              </div>
              {stream && !deleted && !hiddenReplay && <ReportLink stream={stream} />}
              {stream && !deleted && !own && !creatorBlocked && <BlockCreatorLink stream={stream} name={blockName} />}
            </div>
          )}
        </div>
        {!immersive && wide && showChat && <ChatPanel stream={stream!} className="sticky top-4 h-[calc(100dvh-6.5rem)] max-h-[760px] min-h-[440px] rounded-2xl" />}
      </div>
      {chatSheet && stream && <ChatSheet stream={stream} onClose={() => setChatSheet(false)} />}
    </main>
  );
}

/**
 * Who's streaming, under the video: photo, name and @handle. For a creator with a handle it links
 * to their channel's /about view, which (unlike zip2live.com/<handle>) never switches to the live
 * stream, so it works while they're live.
 */
function CreatorStrip({ stream, replay, canFollow }: { stream: PublicStream; replay: boolean; canFollow: boolean }) {
  const { displayName, handle, photoUrl } = stream.channel;
  const name = displayName ?? (handle ? `@${handle}` : 'The creator');
  const live = stream.status === 'LIVE';
  const note = live ? 'live now' : stream.private && replay ? 'private replay, only you can watch' : null;
  const who = (
    <>
      <Avatar name={name} photoUrl={photoUrl} className="h-11 w-11 text-lg" />
      <span className="min-w-0">
        <span className="block truncate font-extrabold group-hover:text-race-300">{name}</span>
        <span className="block truncate text-sm text-faint-dark">
          {handle && displayName ? `@${handle}` : null}
          {handle && displayName && note ? ' · ' : null}
          {note}
        </span>
      </span>
    </>
  );
  const follow = canFollow ? <FollowButton target={{ streamId: stream.id }} name={name} initialFollowers={stream.channel.followers} className="ml-auto shrink-0" /> : null;
  if (!handle) return <div className="mt-3 flex min-w-0 flex-wrap items-center gap-3">{who}{follow}</div>;
  const profile = `/${handle}/about`;
  return (
    <div className="mt-3 flex flex-wrap items-center gap-3">
      <Link to={profile} className="group flex min-w-0 items-center gap-3">
        {who}
      </Link>
      {follow}
      <Link to={profile} className="shrink-0 rounded-full bg-white/10 px-4 py-2 text-sm font-bold hover:bg-white/20">
        View channel
      </Link>
    </div>
  );
}

function DarkHeader() {
  return (
    <Header
      tone="dark"
      right={
        <Link to="/go-live" className="btn-race h-10 px-4 text-sm sm:text-base">
          Go live<span className="hidden sm:inline">&nbsp;yourself</span>
        </Link>
      }
    />
  );
}

function Dark({ children }: { children: ReactNode }) {
  return (
    <main className="min-h-full bg-navy-900 text-white">
      <DarkHeader />
      {children}
    </main>
  );
}

function Chip({ children }: { children: ReactNode }) {
  return <span className="speed-text inline-flex bg-white px-2.5 py-1 text-sm text-navy">{children}</span>;
}

const REPORT_REASONS = [
  ['violence', 'Violence'],
  ['nudity', 'Nudity or sexual content'],
  ['gambling', 'Gambling'],
  ['harassment', 'Harassment or hate'],
  ['mature', 'Not suitable for younger viewers'],
  ['copyright', 'It uses my copyrighted work'],
  ['other', 'Something else'],
] as const;

/**
 * Report button and sheet. The moment of the tap is captured as the wall-clock time of the frame on
 * screen, so moderation reviews what the viewer actually saw (15 s before to 5 s after).
 */
function ReportLink({ stream, className = 'mt-6 block text-xs text-faint-dark' }: { stream: PublicStream; className?: string }) {
  const [frameAt, setFrameAt] = useState<number | null | undefined>(undefined);
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  if (sent) return <p className={className}>Thanks. We’ll review this moment of the stream.</p>;
  const open = frameAt !== undefined;
  const send = async (reason: string) => {
    // Copyright needs a proper notice from the rights holder (pages/Copyright.tsx).
    if (reason === 'copyright') return navigate(`/copyright?stream=${stream.id}`);
    setBusy(true);
    await api.report(stream.id, reason, frameAt).catch(() => undefined);
    setSent(true);
    setFrameAt(undefined);
  };
  return (
    <>
      <button type="button" className={`${className} underline underline-offset-2`} onClick={() => setFrameAt(frameOnScreenAt())}>
        Report this stream
      </button>
      {open && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 sm:items-center" role="dialog" aria-modal="true" aria-labelledby="report-title" onClick={() => setFrameAt(undefined)}>
          <div className="w-full max-w-md rounded-t-[28px] bg-white p-6 pb-[max(1.5rem,env(safe-area-inset-bottom))] text-navy sm:rounded-3xl" onClick={(e) => e.stopPropagation()}>
            <p id="report-title" className="speed-text text-2xl">Report this stream</p>
            <p className="mt-1 text-[15px] text-muted">We’ll review what was on screen when you tapped Report. What’s wrong?</p>
            <div className="mt-4 flex flex-col gap-2">
              {REPORT_REASONS.map(([value, label]) => (
                <button key={value} type="button" disabled={busy} onClick={() => void send(value)} className="h-12 rounded-xl border-2 border-line px-4 text-left font-bold hover:border-navy disabled:opacity-50">
                  {label}
                </button>
              ))}
            </div>
            <button type="button" className="mt-3 h-11 w-full font-bold text-muted underline underline-offset-2" onClick={() => setFrameAt(undefined)}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </>
  );
}
